// Copyright (c) 2026 Broadcom. All rights reserved.
// Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc. and/or its subsidiaries.

import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { API_ENDPOINTS } from './services/api';
import './Login.css';

// Maximum time to wait for the portal silent-auth browser redirect to complete.
// window.location.href fires a full-page navigation; if the network blocks it and
// the browser never leaves the page, this timer resets to the login form so the
// user is not stuck on "Signing you in…" indefinitely.
const SILENT_AUTH_TIMEOUT_MS = 15_000; // 15 seconds

// Map backend auth-error codes (passed via ?auth_error=) to human-readable messages.
// These are surfaced when the OIDC callback fails (session expired, token exchange
// error, AuthHub error) rather than showing a blank login form or looping silently.
const AUTH_ERROR_MESSAGES = {
  session_expired: 'Your sign-in session expired. Please try again.',
  sign_in_failed:  'Sign-in failed. Please refresh the page and try again.',
  access_denied:   'Access was denied. Please contact your administrator if you believe this is an error.',
};

const Login = () => {
  const { login, isAuthenticated, authLoading, authError, devConfig } = useAuth();
  const location = useLocation();
  const from     = location.state?.from?.pathname || '/';

  const [username, setUsername]           = useState('');
  const [password, setPassword]           = useState('');
  const [error, setError]                 = useState('');
  const [loading, setLoading]             = useState(false);
  const [silentChecking, setSilentChecking] = useState(false);

  const portalAuthMode = devConfig?.portalAuthMode === true;
  const portalUrl      = devConfig?.portalUrl || '';

  // Determine whether the silent auth attempt already failed in this navigation.
  // The backend redirects to /login?silent_failed=true when AuthHub returns
  // login_required, so we check for that param to avoid an infinite redirect loop.
  // We also surface an info message so the user knows why the login page appeared.
  const silentFailed  = new URLSearchParams(location.search).get('silent_failed') === 'true';

  const silentFailedMessage =
    "We couldn't sign you in automatically. Please log in again.";

  // Read any auth error code the backend appended to the redirect URL.
  // Used when the OIDC callback fails (session expired, token exchange error, AuthHub error)
  // so the user gets a human-readable message instead of a blank login form.
  const authErrorCode = new URLSearchParams(location.search).get('auth_error');

  // Surface auth_error codes from the URL as a human-readable message on first render.
  useEffect(() => {
    if (authErrorCode) {
      setError(
        AUTH_ERROR_MESSAGES[authErrorCode] ||
        'Sign-in failed. Please try again or contact Broadcom Support.'
      );
    }
  }, [authErrorCode]);

  // On portal-integrated environments, attempt silent OIDC auth before rendering
  // the login form. This lets users who are already logged into the support portal
  // (and therefore have an active AuthHub SSO session) enter CDA without any login UI.
  // Guard: only trigger when portalAuthMode is on AND the session check is done AND
  // the user is not authenticated AND silent auth has not already been attempted.
  useEffect(() => {
    // authError means the user authenticated at AuthHub but belongs to no recognised
    // CDA group (403). authErrorCode means the callback redirected here with a specific
    // error code (e.g. session_expired, sign_in_failed). Do NOT re-trigger silent auth
    // in either case — show the login page so the user can see why and retry manually.
    if (!authLoading && !isAuthenticated && !authError && !authErrorCode && portalAuthMode && !silentFailed) {
      setSilentChecking(true);
      window.location.href = API_ENDPOINTS.SILENT_LOGIN();

      // Safety net: window.location.href fires a full-page navigation, so React
      // normally has no chance to run any more code. But if the network blocks the
      // initial request and the browser never leaves this page, the user would be
      // stuck on "Signing you in…" with no way out.
      // Portal mode: start CDA's own full OIDC login flow. AuthHub handles the
      // authentication and returns the user to the CDA callback — avoiding the
      // Liferay cross-domain ?redirect= restriction that drops users on the
      // portal home page instead of CDA.
      const fallback = setTimeout(() => {
        setSilentChecking(false);
        if (portalAuthMode) {
          window.location.href = API_ENDPOINTS.LOGIN();
        } else {
          setError('Unable to sign you in automatically. Please log in again.');
        }
      }, SILENT_AUTH_TIMEOUT_MS);

      return () => clearTimeout(fallback);
    }
  }, [authLoading, isAuthenticated, authError, authErrorCode, portalAuthMode, silentFailed, portalUrl]);

  // Safety net: if silent_failed lands in the URL while in portal mode (e.g. a
  // bookmarked URL or a stale redirect), start CDA's own OIDC login flow so the
  // user is returned to CDA after authentication rather than landing on the
  // portal home page. The backend no longer produces /login?silent_failed=true
  // in portal mode, so this only fires for manually crafted or cached URLs.
  useEffect(() => {
    if (!authLoading && portalAuthMode && silentFailed && !authError && !authErrorCode) {
      window.location.href = API_ENDPOINTS.LOGIN();
    }
  }, [authLoading, portalAuthMode, silentFailed, authError, authErrorCode]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(err.message || 'Login failed.');
    } finally {
      setLoading(false);
    }
  }, [username, password, login]);

  const handleSSOClick = useCallback(() => {
    window.location.href = API_ENDPOINTS.LOGIN();
  }, []);

  if (authLoading || silentChecking) {
    return null;
  }

  // Portal mode safety net: while the redirect useEffect is about to fire,
  // show a transitional screen so the user never sees the CDA login form.
  if (portalAuthMode && silentFailed && !authError && !authErrorCode) {
    return (
      <div className="login-wrapper">
        <div className="login-backdrop" />
        <div className="login-card">
          <p className="login-loading">Redirecting to the Broadcom Support Portal…</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  // User authenticated at AuthHub (via portal SSO or direct SSO) but their account
  // does not belong to any recognised CDA group — show a clean access-denied screen
  // instead of the login form so the message is clear and unambiguous.
  if (authError) {
    return (
      <div className="login-wrapper">
        <div className="login-backdrop" />
        <div className="login-card">
          <div className="login-logo-section">
            <img
              src="/ICON_Broadcom_Pulse_circle.png"
              alt="Broadcom"
              className="login-logo"
            />
            <h1 className="login-brand-name">Broadcom</h1>
            <p className="login-brand-sub">Code Disk/Strip Application</p>
          </div>
          <div className="login-divider-line" />
          <p className="login-error login-error--access-denied">{authError}</p>
          <p className="login-footer">For authorized users only</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrapper">
      <div className="login-backdrop" />
      <div className="login-card">

        {/* ── Logo section ── */}
        <div className="login-logo-section">
          <img
            src="/ICON_Broadcom_Pulse_circle.png"
            alt="Broadcom"
            className="login-logo"
          />
          <h1 className="login-brand-name">Broadcom</h1>
          <p className="login-brand-sub">Code Disk/Strip Application</p>
        </div>

        <div className="login-divider-line" />

        {/* ── Username / password form — non-portal environments only ── */}
        {/* In portal mode LOCAL_AUTH_BYPASS=false so POST /login always returns 400. */}
        {!portalAuthMode && (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="login-field-group">
              <label className="login-label" htmlFor="login-user">Username</label>
              <input
                id="login-user"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="login-input"
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="login-field-group">
              <label className="login-label" htmlFor="login-pass">Password</label>
              <input
                id="login-pass"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                autoComplete="off"
              />
            </div>

            {silentFailed && !error && (
              <p className="login-info">{silentFailedMessage}</p>
            )}

            {error && <p className="login-error">{error}</p>}

            <button
              type="submit"
              className="login-btn-primary"
              disabled={!username || !password || loading}
            >
              Sign In
            </button>
          </form>
        )}

        {/* ── Portal mode: surface any errors above the SSO button ── */}
        {portalAuthMode && error && <p className="login-error">{error}</p>}

        {/* ── SSO button ── */}
        <button
          type="button"
          className="login-btn-sso"
          onClick={handleSSOClick}
          disabled={loading}
        >
          Login with SSO
        </button>

        <p className="login-footer">For authorized users only</p>

      </div>
    </div>
  );
};

export default Login;
